import { useEffect, useRef, useState } from 'react';

/**
 * A thin, non-intrusive progress bar that slides in under the header while
 * something is loading — the GitHub/YouTube "top bar" pattern.
 *
 * Behaviour (production-standard):
 *  - SHOW DELAY: nothing appears for the first ~150ms. Loads faster than that
 *    are perceived as instant, so showing a bar would only flicker.
 *  - TRICKLE: once visible it eases toward ~90% but never reaches 100% on its
 *    own — it can't know the real progress of a chunk/fetch, so it fakes
 *    forward motion to signal "still working".
 *  - FINISH: when `active` goes false it snaps to 100%, then fades out.
 *
 * Driven by a simple boolean so any loading source can feed it (route chunk
 * loading via Suspense, router navigation, data fetches, …).
 */

const SHOW_DELAY_MS = 150;
const TRICKLE_MS = 300;
const FINISH_MS = 200;

export function TopLoadingBar({ active }: { active: boolean }) {
  // `progress` is 0..100. `visible` controls mount/opacity so we can fade out
  // after hitting 100% rather than vanishing mid-bar.
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);

  const showTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const trickleTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const clearAll = () => {
      if (showTimer.current) clearTimeout(showTimer.current);
      if (trickleTimer.current) clearInterval(trickleTimer.current);
      if (hideTimer.current) clearTimeout(hideTimer.current);
      showTimer.current = null;
      trickleTimer.current = null;
      hideTimer.current = null;
    };

    if (active) {
      clearAll();
      // Wait out the show-delay before committing any UI. If `active` flips
      // back to false before this fires, the bar never appears at all.
      showTimer.current = setTimeout(() => {
        setVisible(true);
        setProgress(8);
        // Ease toward 90% with diminishing steps — slower as it nears the cap.
        trickleTimer.current = setInterval(() => {
          setProgress((p) => (p >= 90 ? p : p + Math.max(0.5, (90 - p) * 0.12)));
        }, TRICKLE_MS);
      }, SHOW_DELAY_MS);
    } else {
      // Either we never showed (fast load) — clear the pending show timer — or
      // we did, in which case snap to 100% and fade out.
      if (showTimer.current) {
        clearTimeout(showTimer.current);
        showTimer.current = null;
      }
      if (trickleTimer.current) {
        clearInterval(trickleTimer.current);
        trickleTimer.current = null;
      }
      setVisible((wasVisible) => {
        if (!wasVisible) return false;
        setProgress(100);
        hideTimer.current = setTimeout(() => {
          setVisible(false);
          setProgress(0);
        }, FINISH_MS);
        return true;
      });
    }

    return clearAll;
  }, [active]);

  if (!visible) return null;

  return (
    <div
      className="pointer-events-none absolute inset-x-0 top-0 z-50 h-0.5 overflow-hidden"
      role="progressbar"
      aria-label="Loading"
      aria-busy="true"
    >
      <div
        className="h-full bg-accent shadow-[0_0_8px_var(--color-accent,currentColor)] transition-[width] duration-300 ease-out"
        style={{ width: `${progress}%`, opacity: progress >= 100 ? 0 : 1 }}
      />
    </div>
  );
}
